import React, { memo, useImperativeHandle, forwardRef, useRef, useEffect, useState } from 'react'
import { IMaskInput } from 'react-imask'
import TextField from '@material-ui/core/TextField'
import MuiInputBase from '@material-ui/core/InputBase'

import { useTheme } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { TextareaAutosize as TextareaAuto } from '@material-ui/core'
import { standardBodyFont } from '../../themes'
import { useBaseComponentOperations, useMergeState } from '../../hooks'
import { translate } from '../../multilanguage'
import { eventObject, defaultVariant } from './common'
import { PreTextEdit } from './'


const outlined = 'outlined'
const labelMarginTop = '-4px'
const outlinedLabelMarginTop = '-7px'

const StyledInput = styled.input`
  width: 100%;
  border: 0px;
  &:focus {
    outline: none;
  }
  padding-top: 6px;
  padding-bottom: 7px;
  background-color: transparent;
  line-height: 0px;
`
const StyledInputUnderline = styled.div`
  width: 100%;
  height: 1px;
`

const StyledUnderline = styled.div`
  width: 100%;
  margin-bottom: -1px;
  height: 2px;
`

const StyledInputHoverUnderline = styled(StyledUnderline)`
  background-color: ${props => props.theme.palette.textFieldHoverUnderlineColor};
`

const StyledInputFocusedUnderline = styled(StyledUnderline)`
  background-color: ${props => props.theme.palette.primary.main};
`

const StyledInputErrorUnderline = styled(StyledUnderline)`
  background-color: ${props => props.theme.palette.error.light};
`

const StyledInputDisabledline = styled(StyledInputUnderline)`
  background-color: ${props => props.theme.palette.action.disabled};
`

export const FastTableTextField = memo(forwardRef((props, ref) => {
  const theme = useTheme()
  const [state, setState] = useMergeState({ color: theme.palette.text.primary }) 
  const { lineHeight, ...standardStyle } = standardBodyFont(theme)

  const onFocus = () => {
    if (props.disabled) {
      return
    }
    setState({ isFocus: true, isHover: false })
  }

  const onBlur = () => {
    if (props.disabled) {
      return
    }
    setState({ isFocus: false, isHover: false })
  }

  const onMouseEnter = () => {
    if (props.disabled) {
      return
    }
    if (state.isFocus) {
      return
    }
    setState({ isFocus: false, isHover: true })
  }

  const onMouseLeave = () => {
    if (props.disabled) {
      return
    }
    setState({ isFocus: state.isFocus, isHover: false })
  }

  useEffect(() => {
    if (props.disabled) {
      setState({ isFocus: false, isHover: false, color: props.theme.palette.action.disabled })
    } else {
      setState({ isFocus: false, isHover: false, color: theme.palette.text.primary })
    }
  }, [props.disabled])

  return (
    <div
      style={{ display: 'inline-block' }}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <StyledInput
        id={props.id}
        disabled={props.disabled}
        value={props.value}
        onChange={props.onChange}
        ref={ref}
        style={{ ...standardStyle, color: state.color }}
      />
      {props.disabled ? <StyledInputDisabledline theme={props.theme} /> : props.error ? <StyledInputErrorUnderline theme={props.theme} /> : state.isHover ? <StyledInputHoverUnderline theme={props.theme} /> : state.isFocus ? <StyledInputFocusedUnderline theme={props.theme}/> : <StyledInputUnderline theme={props.theme} />}
    </div>
  )
}))

FastTableTextField.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  theme: PropTypes.object,
  error: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
}


const TextMaskCustom = memo(forwardRef((props, ref) => {
  const { onChange, ...other } = props
  let mask = props?.placeholder
  const mapObj = {
    a: '[a]',
    9: '[9]',
    A: 'A',
  }
  mask = mask.replace(/a|9/gi, function (matched) {
    return mapObj[matched]
  })

  return (
    <IMaskInput
      {...other}
      mask={mask}
      lazy={true}
      definitions={{
        '#': /[0-9]/,
        A: /[0-9 a-z A-Z]/,
        a: /[0-9 a-z A-Z]?/,
        9: /[0-9]?/,
      }}
      ref={ref}
      onAccept={(value) => {
        onChange({ target: { name: props.name, value } })
      }}
      overwrite
    />
  )
}))

TextMaskCustom.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
}

/**
 * Implements the Mui TextField.
 *
 * @param {object} props component props
 * @returns {TextFieldBase} TextFieldBase component
 */
const TextFieldBase = memo(forwardRef((props, ref) => {
  const { noTranslate, startString, noTranslateStartString, fastTableTextField, type: inputType = 'text', maskedData = { status: false }, ...finalProps } = props
  const eventKey = 'value'
  const theme = useTheme()
  const [value, onChange] = useBaseComponentOperations(props, eventKey, props.defaultValue !== undefined ? props.defaultValue : '')
  const refTextField = useRef()
  const [type, setType] = useState(inputType)

  useImperativeHandle(ref, () => ({
    focus: () => refTextField?.current?.focus(),
    getValue: () => value,
    setValue: newValue => {
      onChange(eventObject(eventKey, newValue))
    },
    setFieldType: value => setType(value)
  }))

  return (
    <>
      {fastTableTextField
        ? <FastTableTextField
            value={value}
            onChange={onChange}
            ref={refTextField}
            theme={theme}
            error={props.error}
            disabled={props.disabled}
          />
        : maskedData.status
          ? <TextField
              variant={props.variant ? props.variant : defaultVariant}
              fullWidth={props.fullWidth}
              {...finalProps}
              placeholder={props.noTranslate ? props.placeholder : translate(props.placeholder)}
              label={typeof props.label !== 'string' ? props.label : props.noTranslate ? props.label : translate(props.label)}
              InputProps={{
                style: { ...standardBodyFont(theme), ...props?.InputProps?.style },
                ...finalProps.InputProps,
                startAdornment: finalProps.InputProps?.startAdornment ? finalProps.InputProps?.startAdornment : startString ? <PreTextEdit noTranslate={noTranslateStartString}>{startString}</PreTextEdit> : undefined,
                endAdornment: finalProps.endAdornment,
                inputComponent: TextMaskCustom,
                // ...props,
              }}
              InputLabelProps={{
                style: { ...standardBodyFont(theme), color: theme.palette.text.primary, marginTop: props.variant === outlined ? outlinedLabelMarginTop : labelMarginTop },
                ...props.InputLabelProps
              }}
              error={props.error}
              value={value}
              onChange={onChange}
              inputRef={refTextField}
              type={type}
              theme={theme}
              name={`test-${props.label}`}
              disabled={props.disabled}
      />
          : <TextField
              variant={props.variant ? props.variant : defaultVariant}
              fullWidth={props.fullWidth}
              {...finalProps}
              placeholder={props.noTranslate ? props.placeholder : translate(props.placeholder)}
              label={typeof props.label !== 'string' ? props.label : props.noTranslate ? props.label : translate(props.label)}
              InputProps={{
                style: { ...standardBodyFont(theme), ...props?.InputProps?.style },
                ...finalProps.InputProps,
                startAdornment: finalProps.InputProps?.startAdornment ? finalProps.InputProps?.startAdornment : startString ? <PreTextEdit noTranslate={noTranslateStartString}>{startString}</PreTextEdit> : undefined,
                endAdornment: finalProps.endAdornment
              }}
              InputLabelProps={{
                style: { ...standardBodyFont(theme), color: theme.palette.text.primary, marginTop: props.variant === outlined ? outlinedLabelMarginTop : labelMarginTop },
                ...props.InputLabelProps
              }}
              error={props.error}
              value={value}
              onChange={onChange}
              inputRef={refTextField}
              type={type}
/>
}
    </>
  )
}))

TextFieldBase.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string, 
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   Attributes applied to the InputLabel element.
   */
  InputLabelProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the startString.
   */
  noTranslateStartString: PropTypes.bool,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', outlined]),
  /**
   If true, the input will take up the full width of its container. Default value is true
   */
  fullWidth: PropTypes.bool,
  /**
   Used to speed up loading for dense table data.
   */
  fastTableTextField: PropTypes.bool,
  type: PropTypes.string,
  // for masked data
  maskedData: PropTypes.objectOf({ status: PropTypes.bool, value: PropTypes.string })
}

TextFieldBase.defaultProps = {
  noTranslate: false,
  variant: defaultVariant,
  fullWidth: true
}

export default TextFieldBase

/**
 * Implements the Mui TextField with the outlined variant.
 *
 * @param {object} props component props
 * @returns {TextFieldOutlined} TextFieldOutlined component
 */
export const TextFieldOutlined = memo(forwardRef((props, ref) =>
  <TextFieldBase
    {...props}
    ref={ref}
    variant={outlined}
  />
))

TextFieldOutlined.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,  
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   Attributes applied to the InputLabel element.
   */
  InputLabelProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   If true, the input will take up the full width of its container. Default value is true
   */
  fullWidth: PropTypes.bool,

}

TextFieldOutlined.defaultProps = {
  disabled: false,
  noTranslate: false,
  fullWidth: true
}

export const TextFieldBorderless = memo(forwardRef((props, ref) => {
  const eventKey = 'value'
  const theme = useTheme()
  const [value, onChange] = useBaseComponentOperations(props, eventKey, '')
  const refTextField = useRef()

  useImperativeHandle(ref, () => ({
    focus: () => refTextField.current.focus(),
    getValue: () => value,
    setValue: newValue => {
      onChange(eventObject(eventKey, newValue))
    }
  }))
  const { startString, fastTableTextField, ...finalProps } = props
  return (
    <>
      {/* eslint-disable-next-line no-console */}
      <MuiInputBase
        variant={props.variant ? props.variant : defaultVariant}
        fullWidth
        placeholder={props.placeholder}
        InputProps={{
          style: { ...standardBodyFont(theme), ...props?.InputProps?.style },
          ...finalProps.InputProps,
        }}
        {...finalProps}
        error={props.error}
        value={value}
        onChange={onChange}
        startAdornment={finalProps.InputProps?.startAdornment ? finalProps.InputProps?.startAdornment : startString ? <PreTextEdit>{startString}</PreTextEdit> : undefined}
        endAdornment={finalProps.endAdornment}
        inputRef={refTextField}
      />
    </>
  )
}))

TextFieldBorderless.propTypes = {  
  /**   
   * id for the control.
   */
  id: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   Attributes applied to the InputLabel element.
   */
  InputLabelProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', outlined]),
  /**
   Used to speed up loading for dense table data.
   */
  fastTableTextField: PropTypes.bool,
}

export const TextareaAutosize = memo(forwardRef((props, ref) => {
  const eventKey = 'value'
  const theme = useTheme()
  const [value, onChange] = useBaseComponentOperations(props, eventKey, '')
  const refTextField = useRef()

  useImperativeHandle(ref, () => ({
    focus: () => refTextField.current.focus(),
    getValue: () => value,
    setValue: newValue => {
      onChange(eventObject(eventKey, newValue))
    }
  }))
  const { startString, fastTableTextField, ...finalProps } = props
  return (
    <>
      {/* eslint-disable-next-line no-console */}
      <TextareaAuto
        variant={props.variant ? props.variant : defaultVariant}
        fullWidth
        placeholder={props.placeholder}
        InputProps={{
          style: { ...standardBodyFont(theme), ...props?.InputProps?.style },
          ...finalProps.InputProps,
        }}
        {...finalProps}
        value={value}
        onChange={onChange}
        startAdornment={finalProps.InputProps?.startAdornment ? finalProps.InputProps?.startAdornment : startString ? <PreTextEdit>{startString}</PreTextEdit> : undefined}
        endAdornment={finalProps.endAdornment}
        inputRef={refTextField}
      />
    </>
  )
}))

TextareaAutosize.propTypes = {  
  /**   
   * id for the control.
   */
  id: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   Attributes applied to the InputLabel element.
   */
  InputLabelProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', outlined]),
  /**
   Used to speed up loading for dense table data.
   */
  fastTableTextField: PropTypes.bool,
}

